import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import { supabase } from '../../lib/supabase';
import { format, addDays } from 'date-fns';

const RETRY_INTERVALS = [1, 3, 7]; // days after failure

const FailedPaymentRecovery = () => {
  const [recoveries, setRecoveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [successMsg, setSuccessMsg] = useState('');
  const [stats, setStats] = useState({ pending: 0, recovered: 0, failed: 0, totalAmount: 0 });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ invoice_id: '', amount: '', failure_code: '', failure_message: '', notes: '' });
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [recRes, invRes] = await Promise.all([
        supabase?.from('failed_payment_recovery')?.select(`
          *,
          invoice:invoices(invoice_number, total_amount, due_date),
          customer:customers(name, email)
        `)?.order('created_at', { ascending: false }),
        supabase?.from('invoices')?.select('id, invoice_number, total_amount, customer:customers(name)')?.eq('invoice_status', 'overdue')?.limit(50)
      ]);

      const data = recRes?.data || [];
      setRecoveries(data);
      setInvoices(invRes?.data || []);

      setStats({
        pending: data?.filter(r => r?.status === 'pending' || r?.status === 'retrying')?.length,
        recovered: data?.filter(r => r?.status === 'recovered')?.length,
        failed: data?.filter(r => r?.status === 'failed')?.length,
        totalAmount: data?.filter(r => r?.status === 'pending' || r?.status === 'retrying')?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0)
      });
    } catch (err) {
      console.error('Fetch error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleRetry = async (recovery) => {
    try {
      const retryCount = (recovery?.retry_count || 0) + 1;
      const maxRetries = recovery?.max_retries || 3;
      const intervalDays = RETRY_INTERVALS?.[Math.min(retryCount - 1, RETRY_INTERVALS?.length - 1)];
      const nextRetry = addDays(new Date(), intervalDays);

      const newStatus = retryCount >= maxRetries ? 'failed' : 'retrying';

      await supabase?.from('failed_payment_recovery')?.update({
        retry_count: retryCount,
        last_retry_at: new Date()?.toISOString(),
        next_retry_at: newStatus === 'retrying' ? nextRetry?.toISOString() : null,
        status: newStatus,
        updated_at: new Date()?.toISOString()
      })?.eq('id', recovery?.id);

      showSuccess(newStatus === 'failed' ? 'Max retries reached. Marked as failed.' : `Retry scheduled for ${format(nextRetry, 'MMM d, yyyy')}`);
      fetchData();
    } catch (err) {
      console.error('Retry error:', err?.message);
    }
  };

  const handleMarkRecovered = async (recovery) => {
    try {
      await supabase?.from('failed_payment_recovery')?.update({
        status: 'recovered',
        recovered_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.eq('id', recovery?.id);

      // Update invoice status if linked
      if (recovery?.invoice_id) {
        await supabase?.from('invoices')?.update({
          invoice_status: 'paid',
          updated_at: new Date()?.toISOString()
        })?.eq('id', recovery?.invoice_id);
      }

      showSuccess('Payment marked as recovered!');
      fetchData();
    } catch (err) {
      console.error('Mark recovered error:', err?.message);
    }
  };

  const handleSendRecoveryEmail = async (recovery) => {
    try {
      await supabase?.from('failed_payment_recovery')?.update({
        recovery_email_sent: true,
        recovery_email_sent_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.eq('id', recovery?.id);
      showSuccess('Recovery email marked as sent');
      fetchData();
    } catch (err) {
      console.error('Email error:', err?.message);
    }
  };

  const handleAddRecovery = async () => {
    if (!addForm?.invoice_id || !addForm?.amount) return;
    setSaving(true);
    try {
      const { data: org } = await supabase?.from('organizations')?.select('id')?.limit(1)?.single();
      const invoice = invoices?.find(i => i?.id === addForm?.invoice_id);
      const nextRetry = addDays(new Date(), RETRY_INTERVALS?.[0]);

      await supabase?.from('failed_payment_recovery')?.insert([{
        organization_id: org?.id,
        invoice_id: addForm?.invoice_id,
        amount: parseFloat(addForm?.amount),
        failure_code: addForm?.failure_code || 'card_declined',
        failure_message: addForm?.failure_message || 'Card was declined',
        notes: addForm?.notes,
        status: 'pending',
        retry_count: 0,
        max_retries: 3,
        next_retry_at: nextRetry?.toISOString()
      }]);

      setShowAddModal(false);
      setAddForm({ invoice_id: '', amount: '', failure_code: '', failure_message: '', notes: '' });
      showSuccess('Failed payment recovery case created');
      fetchData();
    } catch (err) {
      console.error('Add recovery error:', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const filteredRecoveries = recoveries?.filter(r => {
    if (activeTab === 'pending') return r?.status === 'pending' || r?.status === 'retrying';
    if (activeTab === 'recovered') return r?.status === 'recovered';
    if (activeTab === 'failed') return r?.status === 'failed';
    return true;
  });

  const getStatusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-100 text-yellow-800',
      retrying: 'bg-blue-100 text-blue-800',
      recovered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return map?.[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Failed Payment Recovery - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-6 pb-16 px-4 max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <Icon name="AlertTriangle" size={22} color="#dc2626" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Failed Payment Recovery</h1>
              <p className="text-sm text-muted-foreground">Track and recover failed payments with automated retry logic</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Icon name="Plus" size={16} color="currentColor" />
            Add Recovery Case
          </button>
        </div>

        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <Icon name="CheckCircle" size={16} color="#16a34a" />
            <p className="text-sm text-green-700">{successMsg}</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Pending Recovery', value: stats?.pending, icon: 'Clock', color: 'text-yellow-600', bg: 'bg-yellow-50' },
            { label: 'Recovered', value: stats?.recovered, icon: 'CheckCircle', color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Failed (Max Retries)', value: stats?.failed, icon: 'XCircle', color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Amount at Risk', value: `$${stats?.totalAmount?.toFixed(2)}`, icon: 'DollarSign', color: 'text-primary', bg: 'bg-primary/10' },
          ]?.map(stat => (
            <div key={stat?.label} className="bg-card rounded-xl border border-border p-4">
              <div className={`w-8 h-8 ${stat?.bg} rounded-lg flex items-center justify-center mb-2`}>
                <Icon name={stat?.icon} size={16} color="currentColor" className={stat?.color} />
              </div>
              <p className={`text-xl font-bold ${stat?.color}`}>{stat?.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{stat?.label}</p>
            </div>
          ))}
        </div>

        {/* Retry Schedule Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} color="#2563eb" />
            <div>
              <p className="text-sm font-medium text-blue-800">Automated Retry Schedule</p>
              <p className="text-sm text-blue-700 mt-0.5">
                Retry 1: 1 day after failure · Retry 2: 3 days after failure · Retry 3: 7 days after failure · After 3 retries: Marked as failed
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 mb-4">
          {[{ id: 'pending', label: 'Pending / Retrying', count: stats?.pending }, { id: 'recovered', label: 'Recovered', count: stats?.recovered }, { id: 'failed', label: 'Failed', count: stats?.failed }, { id: 'all', label: 'All' }]?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab?.id ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="hidden sm:inline">{tab?.label}</span>
              {tab?.count !== undefined && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab?.id ? 'bg-primary/10 text-primary' : 'bg-muted-foreground/20 text-muted-foreground'
                }`}>{tab?.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Recovery Cases Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredRecoveries?.length === 0 ? (
          <div className="bg-card rounded-xl border border-border p-12 text-center">
            <Icon name="CheckCircle" size={48} color="#16a34a" />
            <p className="mt-3 text-lg font-medium text-foreground">No {activeTab} recovery cases</p>
            <p className="text-sm text-muted-foreground mt-1">Failed payments will appear here when detected</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRecoveries?.map(recovery => (
              <div key={recovery?.id} className="bg-card rounded-xl border border-border p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadge(recovery?.status)}`}>
                        {recovery?.status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Retry {recovery?.retry_count || 0}/{recovery?.max_retries || 3}
                      </span>
                      {recovery?.recovery_email_sent && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Email Sent</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Customer</p>
                        <p className="text-sm font-medium text-foreground">{recovery?.customer?.name || 'N/A'}</p>
                        <p className="text-xs text-muted-foreground">{recovery?.customer?.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Invoice</p>
                        <p className="text-sm font-medium text-foreground">{recovery?.invoice?.invoice_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Amount</p>
                        <p className="text-sm font-bold text-red-600">${parseFloat(recovery?.amount || 0)?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Failure Reason</p>
                        <p className="text-sm text-foreground">{recovery?.failure_message || recovery?.failure_code || 'Unknown'}</p>
                      </div>
                      {recovery?.next_retry_at && recovery?.status === 'retrying' && (
                        <div>
                          <p className="text-xs text-muted-foreground">Next Retry</p>
                          <p className="text-sm text-blue-600">{format(new Date(recovery?.next_retry_at), 'MMM d, yyyy')}</p>
                        </div>
                      )}
                      {recovery?.recovered_at && (
                        <div>
                          <p className="text-xs text-muted-foreground">Recovered</p>
                          <p className="text-sm text-green-600">{format(new Date(recovery?.recovered_at), 'MMM d, yyyy')}</p>
                        </div>
                      )}
                    </div>
                    {recovery?.notes && (
                      <p className="text-xs text-muted-foreground mt-2 italic">{recovery?.notes}</p>
                    )}
                  </div>

                  {(recovery?.status === 'pending' || recovery?.status === 'retrying') && (
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <button
                        onClick={() => handleRetry(recovery)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                      >
                        <Icon name="RefreshCw" size={12} color="currentColor" />
                        Retry Now
                      </button>
                      <button
                        onClick={() => handleMarkRecovered(recovery)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors"
                      >
                        <Icon name="CheckCircle" size={12} color="currentColor" />
                        Mark Recovered
                      </button>
                      {!recovery?.recovery_email_sent && (
                        <button
                          onClick={() => handleSendRecoveryEmail(recovery)}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-border text-foreground rounded-lg text-xs font-medium hover:bg-muted transition-colors"
                        >
                          <Icon name="Mail" size={12} color="currentColor" />
                          Send Email
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Recovery Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-md">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Add Recovery Case</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 hover:bg-muted rounded-lg">
                <Icon name="X" size={18} color="var(--color-foreground)" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Overdue Invoice</label>
                <select
                  value={addForm?.invoice_id}
                  onChange={(e) => {
                    const inv = invoices?.find(i => i?.id === e?.target?.value);
                    setAddForm(prev => ({ ...prev, invoice_id: e?.target?.value, amount: inv?.total_amount || '' }));
                  }}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">Select invoice...</option>
                  {invoices?.map(inv => (
                    <option key={inv?.id} value={inv?.id}>
                      {inv?.invoice_number} - {inv?.customer?.name} (${parseFloat(inv?.total_amount || 0)?.toFixed(2)})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Amount Failed ($)</label>
                <input
                  type="number"
                  value={addForm?.amount}
                  onChange={(e) => setAddForm(prev => ({ ...prev, amount: e?.target?.value }))}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Failure Code</label>
                <select
                  value={addForm?.failure_code}
                  onChange={(e) => setAddForm(prev => ({ ...prev, failure_code: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="card_declined">Card Declined</option>
                  <option value="insufficient_funds">Insufficient Funds</option>
                  <option value="expired_card">Expired Card</option>
                  <option value="incorrect_cvc">Incorrect CVC</option>
                  <option value="processing_error">Processing Error</option>
                  <option value="do_not_honor">Do Not Honor</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Notes</label>
                <textarea
                  value={addForm?.notes}
                  onChange={(e) => setAddForm(prev => ({ ...prev, notes: e?.target?.value }))}
                  placeholder="Additional notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowAddModal(false)} className="flex-1 py-2 border border-border text-foreground rounded-lg text-sm hover:bg-muted transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleAddRecovery}
                  disabled={saving || !addForm?.invoice_id || !addForm?.amount}
                  className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {saving ? 'Creating...' : 'Create Case'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FailedPaymentRecovery;
